import type { GraphqlError } from './error-handling/error-component';

import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, from } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { setContext } from '@apollo/client/link/context';
import { relayStylePagination } from '@apollo/client/utilities';
import { type ReactNode, useMemo, useState } from 'react';

import { ENV } from './config/env';
import { ErrorComponent } from './error-handling/error-component';
import { problemsHandler } from './error-handling/problems-handler';
import { keycloak } from './service/authentication/keycloak-provider';
import { devLog } from './utils';
import { mergeById, mergeByUniqueProp } from './utils/apollo-helpers';
import { removeSessionStorageItem } from './utils/session-storage';
import { viewerFetch } from './utils/viewer-fetch';

const MIN_VALIDITY_SECONDS = 300;

if (ENV.MODE === 'development') {
  loadErrorMessages();
  loadDevMessages();
}

const adminHeaders = setContext(async (_, { headers }) => {
  // Refresh token if expired
  await keycloak.updateToken(MIN_VALIDITY_SECONDS).catch(keycloak.login);

  return {
    headers: {
      ...headers,
      authorization: `Bearer ${keycloak?.token}`,
      'content-type': 'application/json',
    },
  };
});

export function AdminApiProvider({ children }: { children: ReactNode }) {
  const [error, setError] = useState<GraphqlError>();

  const adminClient = useMemo(
    () =>
      new ApolloClient({
        link: from([adminHeaders, problemsHandler(setError), new HttpLink({ uri: ENV.ADMIN_APOLLO_CLIENT_URI })]),
        cache: new InMemoryCache({
          typePolicies: {
            Webcast: {
              merge: true,
              fields: {
                contents: { merge: mergeByUniqueProp('language') },
                recordingSettings: { merge: true },
                subtitleSettings: { merge: true },
                // Always the complete speaker list — replace wholesale instead of merging.
                speakers: { merge: false },
              },
            },
            CurrentUser: { merge: true },
            LiveStreamingSettings: { merge: true },
            StandardLanguage: { merge: true },
            // `keyFields` specifies `lsproId` as the unique identifier for caching
            LiveStreamProSettings: {
              keyFields: ['lsproId'],
              merge: true,
              fields: {
                themes: { merge: mergeById },
                supportedFonts: { merge: mergeById },
              },
            },
            Query: {
              fields: {
                currentUser: { merge: true },
                configuration: { merge: true },
                lsproSettings: { merge: true },
                webcast: { merge: true },
                webcasts: relayStylePagination(),
              },
            },
          },
        }),
      }),
    []
  );

  return (
    <ApolloProvider client={adminClient}>
      <ErrorComponent error={error} onClose={() => setError(undefined)}>
        {children}
      </ErrorComponent>
    </ApolloProvider>
  );
}

const viewerHeaders = setContext((_, { headers }) => {
  const data = {
    headers: {
      ...headers,
      ...(window.document.referrer ? { 'X-Referrer': new URL(window.document.referrer).hostname } : {}),
      'content-type': 'application/json',
    },
  };

  devLog('👥 Current API headers:', data.headers, { memoize: true });

  return data;
});

// Clear Old-Last-Modified entries on mount to avoid stale data across sessions
Object.keys(sessionStorage).forEach((key) => {
  if (key === 'Old-Last-Modified' || key.startsWith('Old-Last-Modified:')) {
    removeSessionStorageItem(key);
  }
});

export function ViewerApiProvider({ children }: { children: ReactNode }) {
  const [error, setError] = useState<GraphqlError>();

  const viewerClient = useMemo(
    () =>
      new ApolloClient({
        cache: new InMemoryCache(),
        link: from([
          viewerHeaders,
          problemsHandler(setError),
          new HttpLink({ uri: ENV.VIEWER_APOLLO_CLIENT_URI, fetch: viewerFetch }),
        ]),
      }),
    []
  );

  return (
    <ApolloProvider client={viewerClient}>
      <ErrorComponent error={error} onClose={() => setError(undefined)}>
        {children}
      </ErrorComponent>
    </ApolloProvider>
  );
}
